import z from 'zod';
import { AllCharLabelSchema } from '../common/schemas';
import { DBCourseCourseIdSchema } from '../courses/schemas';
import { DBPermissionPermissionIdSchema } from '../permissions/schemas';
import { QuestionSetIdSchema } from '../questionSets/schemas';
import { LessonType } from './enums';

/**
 * Shared & Helper Schemas
 */

export const DBLessonTemplateCourseLessonIdSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 2 &&
    z.lazy(() => DBLessonTemplateCourseSchema).safeParse(parts[0]).success &&
    z.lazy(() => DBLessonTemplateLessonIdSchema).safeParse(parts[1]).success
  );
}, 'Must be in format course#lessonId');

/**
 * DB Schemas
 */

export const DBLessonTemplateActiveSchema = z.boolean();
export const DBLessonTemplateCourseSchema = z.lazy(() => DBCourseCourseIdSchema);
export const DBLessonTemplateLessonIdSchema = z
  .string()
  .regex(/^[a-zA-Z0-9]+$/)
  .min(1)
  .max(8)
  .openapi({
    description: 'Abbreviated unique ID for the lesson',
    example: 'M5L1',
  });
export const DBLessonTemplateDurationSchema = z.number().int().min(0).max(1440);
export const DBLessonTemplateSortOrderSchema = z.number().int().min(0);
export const DBLessonTemplateQuizSchema = z.lazy(() => QuestionSetIdSchema);
export const DBLessonTemplateHomeworkRequiredSchema = z.boolean();
export const DBLessonTemplateIsTimesheetedSchema = z.boolean();
export const DBLessonTemplateLabelSchema = AllCharLabelSchema.min(1).max(64);
export const DBLessonTemplateLessonGroupSchema = AllCharLabelSchema.min(1).max(32);
export const DBLessonTemplateLessonTypeSchema = z.enum(LessonType);
export const DBLessonTemplatePermissionsSchema = z.record(
  z.lazy(() => DBPermissionPermissionIdSchema),
  z.object({}).strict()
);

export const DBLessonTemplateSchema = z.object({
  course: DBLessonTemplateCourseSchema,
  lessonId: DBLessonTemplateLessonIdSchema,
  active: DBLessonTemplateActiveSchema,
  duration: DBLessonTemplateDurationSchema,
  homeworkRequired: DBLessonTemplateHomeworkRequiredSchema,
  isTimesheeted: DBLessonTemplateIsTimesheetedSchema,
  label: DBLessonTemplateLabelSchema,
  lessonGroup: DBLessonTemplateLessonGroupSchema,
  lessonType: DBLessonTemplateLessonTypeSchema,
  // .optional() indicates to ElectroDB that keys of this dynamic field may be deleted
  permissions: DBLessonTemplatePermissionsSchema.optional(),
  // .optional(): unset until the course's templates are first reordered
  sortOrder: DBLessonTemplateSortOrderSchema.optional(),
  // .optional(): unset while the template has no preset quiz
  quiz: DBLessonTemplateQuizSchema.optional(),
});

/**
 * Client Schemas
 */

export const LessonTemplateActiveSchema = DBLessonTemplateActiveSchema;
export const LessonTemplateCourseSchema = DBLessonTemplateCourseSchema;
export const LessonTemplateLessonIdSchema = DBLessonTemplateLessonIdSchema;
export const LessonTemplateDurationSchema = DBLessonTemplateDurationSchema;
export const LessonTemplateHomeworkRequiredSchema = DBLessonTemplateHomeworkRequiredSchema;
export const LessonTemplateIsTimesheetedSchema = DBLessonTemplateIsTimesheetedSchema;
export const LessonTemplateLabelSchema = DBLessonTemplateLabelSchema;
export const LessonTemplateLessonGroupSchema = DBLessonTemplateLessonGroupSchema;
export const LessonTemplateLessonTypeSchema = DBLessonTemplateLessonTypeSchema;
export const LessonTemplatePermissionsSchema = z.array(
  z.lazy(() => DBPermissionPermissionIdSchema)
);
export const LessonTemplateSortOrderSchema = DBLessonTemplateSortOrderSchema;
export const LessonTemplateQuizSchema = DBLessonTemplateQuizSchema;

export const LessonTemplateSchema = z.object({
  course: LessonTemplateCourseSchema,
  lessonId: LessonTemplateLessonIdSchema,
  active: LessonTemplateActiveSchema.default(true),
  duration: LessonTemplateDurationSchema.default(120),
  homeworkRequired: LessonTemplateHomeworkRequiredSchema.default(true),
  isTimesheeted: LessonTemplateIsTimesheetedSchema.default(true),
  label: LessonTemplateLabelSchema,
  lessonGroup: LessonTemplateLessonGroupSchema,
  lessonType: LessonTemplateLessonTypeSchema,
  permissions: LessonTemplatePermissionsSchema.default([]),
  sortOrder: LessonTemplateSortOrderSchema.optional(),
  quiz: LessonTemplateQuizSchema.optional(),
});
